import React, { useEffect, useRef, useState } from 'react';
import { X, TrendingUp, Square, AlertTriangle } from 'lucide-react';

// Kademeli pozisyon motoru — grubu HEDEF boyuta götürür.
//
// Hedge popup'ından farkı: orada tek bacak sabit bir tavana kadar doldurulur.
// Burada iki bacak birlikte, tekrar tekrar, yalnız borsalar arası fark yeterince
// iyiyken büyür (ya da küçülür — hedef mevcuttan azsa aynı motor azaltır).
//
// Eşik NET: komisyon düşülmüş fark. Üstelik en kötü hal (öncü maker + takipçi
// taker) varsayılıyor, yoksa artı sanılan giriş eksiye dönebilir.

export interface ScalePrefill {
  groupNo: number;
  legA: { botId: string; symbol: string; side: 'long' | 'short' };
  legB: { botId: string; symbol: string; side: 'long' | 'short' };
  currentSize: number;   // bacak başına mevcut (mutlak)
  tickSize?: number;     // öncü bacağın tik adımı — yalnız bilgi amaçlı
}

interface Props {
  dark?: boolean;
  prefill: ScalePrefill;
  sendWs: (m: any) => void;
  subscribe: (fn: (m: any) => void) => (() => void) | undefined;
  onClose: () => void;
}

export default function ScaleModal({ dark = true, prefill, sendWs, subscribe, onClose }: Props) {
  const [target, setTarget] = useState(String(prefill.currentSize || ''));
  const [clip, setClip]     = useState('0.5');
  // Eşik BPS: komisyonla aynı birim olduğu için doğrudan kıyaslanabiliyor
  // ("16 bps fark, 5 bps komisyon"). Tik pariteler arası 200 kat değişiyor
  // (WAL 4,22 bps/tik · BTC 0,02), o yüzden yalnız BİLGİ olarak gösteriliyor.
  const [bps, setBps]       = useState('13');
  // Kurarken USD karşılığını gösterebilmek için iki bacağın fiyat + komisyonu.
  const [qa, setQa]         = useState<any>(null);
  const [qb, setQb]         = useState<any>(null);
  const [job, setJob]       = useState<any>(null);
  const [msg, setMsg]       = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy]     = useState(false);

  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const inp   = dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                     : 'bg-white border-[#d4e0f0] text-slate-800';

  const poll = useRef<any>(null);
  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type === 'scale_started') {
        setBusy(false);
        setMsg(m.payload?.success
          ? { ok: true, text: 'İş başladı.' }
          : { ok: false, text: m.payload?.error || 'başlatılamadı' });
      } else if (m.type === 'scale_status') {
        setJob((m.payload?.jobs || []).find((x: any) => x.groupNo === prefill.groupNo) || null);
      } else if (m.type === 'leg_quote') {
        if (m.bot_id === prefill.legA.botId) setQa(m.payload);
        else if (m.bot_id === prefill.legB.botId) setQb(m.payload);
      } else if (m.type === 'scale_stopped') {
        setMsg({ ok: true, text: 'Durdurma gönderildi.' });
      }
    });
    return () => unsub?.();
  }, [subscribe, prefill.groupNo, prefill.legA.botId, prefill.legB.botId]);

  useEffect(() => {
    for (const l of [prefill.legA, prefill.legB]) {
      sendWs({ type: 'get_leg_quote', payload: { botId: l.botId, symbol: l.symbol } });
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ask = () => sendWs({ type: 'scale_status', payload: { groupNo: prefill.groupNo } });
    ask();
    poll.current = setInterval(ask, 2000);
    return () => clearInterval(poll.current);
  }, [sendWs, prefill.groupNo]);

  const t = Number(target) || 0;
  const c = Number(clip) || 0;
  const nb = Number(bps) || 0;
  // Referans fiyat öncü bacaktan; USD karşılıkları buradan türüyor.
  const px = Number(qa?.mark || qa?.best_ask || qa?.best_bid || 0);
  // Motorun kullandığı EN KÖTÜ hal ile aynı: öncü maker + takipçi taker.
  const costBps = ((Number(qa?.maker_fee) || 0) + (Number(qb?.taker_fee) || 0)) * 100;
  const tickBps = prefill.tickSize && px ? (prefill.tickSize / px) * 10000 : 0;
  const remaining = Math.abs(t - prefill.currentSize);
  const usd = (qty: number, b: number) => (px > 0 ? (qty * px * b) / 10000 : 0);
  const clipEdge = usd(c, nb);
  const totEdge  = usd(remaining, nb);
  const clipFee  = usd(c, costBps);
  const totFee   = usd(remaining, costBps);
  const money = (v: number) => `$${v.toFixed(v < 1 ? 4 : 2)}`;
  const grow = t > prefill.currentSize;
  const steps = c > 0 ? Math.ceil(Math.abs(t - prefill.currentSize) / c) : 0;
  const running = job?.state === 'running';

  const fire = () => {
    if (t <= 0 || c <= 0) { setMsg({ ok: false, text: 'Hedef ve klip pozitif olmalı.' }); return; }
    setBusy(true); setMsg(null);
    sendWs({ type: 'scale_start', payload: {
      groupNo: prefill.groupNo, legA: prefill.legA, legB: prefill.legB,
      target: t, clip: c, minNetBps: Number(bps) || 0,
    } });
  };

  const legLine = (l: ScalePrefill['legA']) => (
    <span className="font-mono">
      <span className={l.side === 'long' ? 'text-emerald-400' : 'text-rose-400'}>
        {l.side.toUpperCase()}
      </span>{' '}{l.symbol} <span className={muted}>· {l.botId}</span>
    </span>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-[560px] max-w-full rounded-xl border ${card} p-4 shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={15} className="text-emerald-400" />
          <span className="font-semibold text-sm">Pozisyonu hedefe götür — Grup {prefill.groupNo}</span>
          <button onClick={onClose} className={`ml-auto ${muted} hover:text-rose-400`}><X size={16} /></button>
        </div>

        <div className={`rounded-lg border ${sub} p-2.5 mb-3 text-[11px] space-y-1`}>
          <div className={muted}>Bacaklar</div>
          <div>{legLine(prefill.legA)}</div>
          <div>{legLine(prefill.legB)}</div>
          <div className={`${muted} pt-1`}>
            Mevcut <span className="font-mono">{prefill.currentSize}</span> · hedef{' '}
            <span className="font-mono text-emerald-400">{t || '—'}</span>
            {steps > 0 && <> · {steps} klip {grow ? 'ekleyerek' : 'azaltarak'}</>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <label className="flex flex-col gap-1">
            <span className={muted}>Hedef (bacak başına)</span>
            <input value={target} onChange={e => setTarget(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted}>Klip</span>
            <input value={clip} onChange={e => setClip(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted} title="Komisyon düşülmüş fark. En kötü hal (öncü maker + takipçi taker) varsayılır.">
              Asgari NET fark (bps)
              {tickBps > 0 && (
                <span className="opacity-60"> · ≈{(nb / tickBps).toFixed(1)} tik</span>
              )}
            </span>
            <input value={bps} onChange={e => setBps(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
        </div>

        {px > 0 && nb > 0 && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-1`}>
            <div className={`${muted} flex items-center gap-1`}>
              Bu eşikte GİRİŞ FARKI
              <span className="opacity-60" title="Yalnız giriş farkı — kâr değil. Funding henüz gelmedi, çıkış komisyonu henüz ödenmedi.">
                (kâr değil)
              </span>
            </div>
            <div className="flex justify-between">
              <span className={muted}>Klip başına ({c || '—'})</span>
              <span className="tabular-nums">
                <span className="text-emerald-400">{money(clipEdge)}</span>
                <span className={muted}> · komisyon </span>
                <span className="text-rose-400">−{money(clipFee)}</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className={muted}>Hedefe kadar ({remaining || '—'})</span>
              <span className="tabular-nums">
                <span className="text-emerald-400">{money(totEdge)}</span>
                <span className={muted}> · komisyon </span>
                <span className="text-rose-400">−{money(totFee)}</span>
              </span>
            </div>
            {costBps > 0 && (
              <div className={muted}>
                komisyon {costBps.toFixed(1)} bps · referans fiyat {px}
                {clipEdge > 0 && clipFee > 0 && (
                  <> · {money(clipEdge)} kazanmak için {money(clipFee)} ödüyorsun</>
                )}
              </div>
            )}
          </div>
        )}

        <p className={`mt-2 text-[11px] ${muted}`}>
          Öncü bacak maker yazılır; <span className="text-blue-200">dolmadan</span> takipçi
          gönderilmez — açık delta en fazla bir klip. Takipçi maker dolmazsa taker'a geçer.
          İki bacak eşitsizse fark koşulu aranmadan önce denge kapatılır.
        </p>

        {job && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px] space-y-1`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                job.state === 'running' ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                : job.state === 'done' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`}>
                {job.state}
              </span>
              <span className="tabular-nums">A {job.sizeA} · B {job.sizeB} / {job.target}</span>
              <span className={muted}>· {job.clips} klip</span>
              {job.lastNetBps != null && (
                <span className={`ml-auto tabular-nums ${job.lastNetBps >= job.minNetBps
                  ? 'text-emerald-400' : 'text-amber-400'}`}
                  title={`eşik ${job.minNetTicks} tik = ${Number(job.minNetBps).toFixed(1)} bps`}>
                  net {job.lastNetBps} bps
                  {job.lastNetTicks != null && (
                    <span className="opacity-70"> ({job.lastNetTicks} tik)</span>
                  )}
                </span>
              )}
            </div>
            {(job.phase || job.reason) && (
              <div className={muted}>{job.reason || job.phase}</div>
            )}
          </div>
        )}

        {msg && (
          <p className={`mt-2 text-[11px] break-words flex items-start gap-1.5 ${msg.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
            {!msg.ok && <AlertTriangle size={12} className="mt-0.5 shrink-0" />}
            {msg.text}
          </p>
        )}

        <div className="flex items-center gap-2 mt-3">
          {!running ? (
            <button onClick={fire} disabled={busy}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600/25 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-600/40 disabled:opacity-40">
              {busy ? 'Başlatılıyor…' : 'Başlat — emir gönderir'}
            </button>
          ) : (
            <button onClick={() => sendWs({ type: 'scale_stop', payload: { groupNo: prefill.groupNo } })}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-rose-600/25 border border-rose-500/50 text-rose-300 hover:bg-rose-600/40 flex items-center justify-center gap-1.5">
              <Square size={12} /> Durdur
            </button>
          )}
          <button onClick={onClose} className={`px-3 py-2 rounded-lg text-xs border ${sub} ${muted}`}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
